import { FC, useEffect } from 'react';
import { Button, Radio } from 'components/core';

import {
  getMailingNameFromAddress,
  getNameFromAddress,
  getSelectedBillingAddress,
  getSelectedShippingAddress,
  OrderAddressProps,
  Styled,
} from '.';
import { Cart } from 'types';
import { useAddresses } from 'reactQuery/queries';
import { LoadingSpinner, LoadingWrapper } from 'styles/sharedStyles';

import { ServerStateKeysEnum } from 'reactQuery/constants';
import { useQueryClient } from 'react-query';
import { useI18n } from 'contexts/i18n';
import {
  NotificationBody,
  NotificationIcon,
  Notification,
} from 'components/ITeroOrderPayment/ITeroOrderPayment.styles';
import { getIconByNotificationType } from 'components/core/Notification';
import ReactToolTip from 'react-tooltip';
import { isEMEAStore } from 'utils';

const IteroOrderAddress: FC<OrderAddressProps> = ({
  mode,
  selectedShippingAddress,
  selectedBillingAddress,
  setBillingAddress,
  setShippingAddress,
  onSave,
  tmSelectedBillAddressId,
  tmSelectedShipAddressId,
}) => {
  const i18n = useI18n();
  const queryClient = useQueryClient();
  const currentOrder = queryClient.getQueryData<Cart>(
    ServerStateKeysEnum.userCurrentCart
  );
  const {
    data: address,
    isLoading: isAddressLoading,
    isFetching: isAddressFetching,
    isIdle,
    isError,
  } = useAddresses();

  useEffect(() => {
    if (address) {
      setBillingAddress(
        getSelectedBillingAddress(tmSelectedBillAddressId, address)
      );
      setShippingAddress(
        getSelectedShippingAddress(tmSelectedShipAddressId, address)
      );
    }
  }, [address, currentOrder, setBillingAddress, setShippingAddress]);

  if (isAddressLoading || isIdle || isAddressFetching) {
    return (
      <LoadingWrapper>
        <LoadingSpinner width="25px" height="25px" isPrimary={true} />
      </LoadingWrapper>
    );
  }
  if (isError) {
    return (
      <Styled.ErrorSection>
        {
          i18n[
            'Unable to fetch shipping and billing address, please try again later.'
          ]
        }
      </Styled.ErrorSection>
    );
  }

  const notificationBody = () => {
    return (
      i18n[
        'Please note: The address selected for shipping is where the iTero Scanner will be registered.'
      ] +
      (!isEMEAStore()
        ? i18n[
            'Please contact your Align representative if you wish to change the shipping address.'
          ]
        : '')
    );
  };

  const getAddressTooltipText = (shouldDisableAddressSelection: boolean) => {
    return shouldDisableAddressSelection
      ? i18n[
          'Please contact your Align representative if you wish to change the shipping address.'
        ]
      : '';
  };

  return (
    <>
      {mode === 'create' && (
        <Styled.OrderAddressesCreateContainer>
          <Notification>
            <NotificationIcon>
              {getIconByNotificationType('info')}
            </NotificationIcon>
            <NotificationBody>{notificationBody()}</NotificationBody>
          </Notification>
          <Styled.ShippingAddress>
            <Styled.Title>{i18n['Select Ship to']}</Styled.Title>
            <Styled.AddressContainerWrapper>
              {address?.shippingAddresses.map(shippingAddress => {
                const shouldDisableAddressSelection =
                  !isEMEAStore() &&
                  shippingAddress.salesforceId !== tmSelectedShipAddressId;
                return (
                  <Styled.AddressContainer
                    key={shippingAddress.id}
                    isSelected={
                      shippingAddress.id === selectedShippingAddress?.id
                    }
                    onClick={() => {
                      if (!shouldDisableAddressSelection) {
                        setShippingAddress(shippingAddress);
                      }
                      return;
                    }}
                    isDisabled={shouldDisableAddressSelection}
                    data-tip={getAddressTooltipText(
                      shouldDisableAddressSelection
                    )}
                    data-for={`address-tooltip-${shippingAddress.id}`}
                  >
                    <Radio
                      checked={
                        shippingAddress.id === selectedShippingAddress?.id
                      }
                      onChange={() => setShippingAddress(shippingAddress)}
                      disabled={shouldDisableAddressSelection}
                    />
                    <Styled.AddressWrapper>
                      {getNameFromAddress(shippingAddress).length > 0 ? (
                        <Styled.MailingName>
                          {getNameFromAddress(shippingAddress)}
                        </Styled.MailingName>
                      ) : (
                        ''
                      )}
                      <Styled.MailingName>
                        {getMailingNameFromAddress(shippingAddress)}
                      </Styled.MailingName>
                      <Styled.Address>
                        {shippingAddress.address1} {shippingAddress.address2}{' '}
                        {shippingAddress.address3} {shippingAddress.city},{' '}
                        {shippingAddress.stateName} {shippingAddress.zipcode},{' '}
                        {shippingAddress.countryIso}
                      </Styled.Address>
                    </Styled.AddressWrapper>

                    <ReactToolTip
                      id={`address-tooltip-${shippingAddress.id}`}
                      backgroundColor="rgba(2, 119, 180, 1)"
                      place="top"
                      effect="solid"
                      className="tooltip"
                    />
                  </Styled.AddressContainer>
                );
              })}
            </Styled.AddressContainerWrapper>
          </Styled.ShippingAddress>

          <Styled.BillingAddress>
            <Styled.Title>{i18n['Select Bill to']}</Styled.Title>

            {
              <div>
                {address?.billingAddresses.map(billingAddress => {
                  return (
                    <Styled.AddressContainer
                      key={billingAddress.id}
                      isSelected={
                        billingAddress.id === selectedBillingAddress?.id
                      }
                      onClick={() => setBillingAddress(billingAddress)}
                    >
                      <Radio
                        checked={
                          billingAddress.id === selectedBillingAddress?.id
                        }
                        onChange={() => setBillingAddress(billingAddress)}
                      />
                      <Styled.AddressWrapper>
                        {getNameFromAddress(billingAddress).length > 0 ? (
                          <Styled.MailingName>
                            {getNameFromAddress(billingAddress)}
                          </Styled.MailingName>
                        ) : (
                          ''
                        )}
                        <Styled.MailingName>
                          {getMailingNameFromAddress(billingAddress)}
                        </Styled.MailingName>
                        <Styled.Address>
                          {billingAddress.address1} {billingAddress.address2}{' '}
                          {billingAddress.address3} {billingAddress.city},{' '}
                          {billingAddress.stateName} {billingAddress.zipcode},{' '}
                          {billingAddress.countryIso}
                        </Styled.Address>
                      </Styled.AddressWrapper>
                    </Styled.AddressContainer>
                  );
                })}
              </div>
            }
          </Styled.BillingAddress>

          <Styled.ButtonWrapper>
            <Button
              size="md"
              onClick={onSave}
              disabled={
                typeof selectedBillingAddress?.id === 'undefined' ||
                typeof selectedShippingAddress?.id === 'undefined'
              }
            >
              {i18n['Next']}
            </Button>
          </Styled.ButtonWrapper>
        </Styled.OrderAddressesCreateContainer>
      )}
      {mode === 'edit' && (
        <Styled.OrderAddressesEditContainer>
          <Styled.SelectedAddressContainer>
            <Styled.Title>{i18n['Ship to']}</Styled.Title>
            <Styled.SelectedAddress>
              {getNameFromAddress(selectedShippingAddress).length > 0 ? (
                <>
                  {getNameFromAddress(selectedShippingAddress)} <br />
                </>
              ) : (
                ''
              )}
              {getMailingNameFromAddress(selectedShippingAddress)} <br />
              {selectedShippingAddress?.address1}{' '}
              {selectedShippingAddress?.address2}{' '}
              {selectedShippingAddress?.address3}
              <br />
              <span>
                {selectedShippingAddress?.city},{' '}
                {selectedShippingAddress?.stateName}{' '}
                {selectedShippingAddress?.zipcode}
              </span>
            </Styled.SelectedAddress>
          </Styled.SelectedAddressContainer>

          <Styled.SelectedAddressContainer>
            <Styled.Title>{i18n['Bill to']}</Styled.Title>
            <Styled.SelectedAddress>
              {getNameFromAddress(selectedBillingAddress).length > 0 ? (
                <>
                  {getNameFromAddress(selectedBillingAddress)} <br />
                </>
              ) : (
                ''
              )}
              {getMailingNameFromAddress(selectedBillingAddress)}
              <br />
              {selectedBillingAddress?.address1}{' '}
              {selectedBillingAddress?.address2}{' '}
              {selectedBillingAddress?.address3}
              <br />
              <span>
                {selectedBillingAddress?.city},{' '}
                {selectedBillingAddress?.stateName}{' '}
                {selectedBillingAddress?.zipcode}
              </span>
            </Styled.SelectedAddress>
          </Styled.SelectedAddressContainer>
        </Styled.OrderAddressesEditContainer>
      )}
    </>
  );
};

export default IteroOrderAddress;
